import React from 'react';
import * as translator from '../../utils/translator';
import OrderWindow from '../../components/OrderWindow/OrderWindow';
import OrderOTCAddons from '../../components/OrderOTCAddons/OrderOTCAddons';
import OrderSummary from '../../components/OrderSummary/OrderSummary';
import CartViewDE from '../../components/CartViewDE';
import CartView from '../../components/CartView';
import CartViewFAQ from '../../components/CartViewFAQ';
import TrustedShopsBadge from '../../components/Trustedshops/TrustedShopsBadge';
import { ORDER_FLOWS, SWITZERLAND } from '../../utils/consts';
import type { OrderWindowDetails } from './paymentTypes';
import { TRUSTEDSHOP_COUNTRIES, COUNTRIES_WITH_TREATMENT_FEE } from './paymentConstants';
import { shouldShowPriceBreakdown } from '../../utils/helpers';
import { useCartContext } from '../../customHooks/useCart';


interface OrderSidebarProps {
    country: string;
    language: string;
    orderState: any;
    orderWindowDetails: OrderWindowDetails;
    OTCProducts: any[];
    otcDiscount: number;
    shippingFee: number;
    currencyRate: string;
    currencySign: string;
    rxProductFinalPrice: number;
    setRxProductFinalPrice: (price: number) => void;
    isCannabisCatalog: boolean;
    testLabSampleType?: string | null;
    productsGroupedByPharmacy: any[];
    onExpandProducts: () => void;
    onTooltipOpen: (tooltipId: string) => void;
    showDelayedCaptureDisclaimer?: boolean;
}


// ============================================================================
// Helper Functions
// ============================================================================

function isNewFlow(flow: string | null): boolean {
    if (!flow) return false;
    
    const newFlows = [
        ORDER_FLOWS.CANNALEO_PRE_SELECTED,
        ORDER_FLOWS.MULTIPLE_PRODUCTS,
        ORDER_FLOWS.SCHENGEN,
        ORDER_FLOWS.FLOWZZ_PRE_SELECTED,
        ORDER_FLOWS.OTC_ONLY,
    ];
    
    return newFlows.includes(flow) || flow.includes(ORDER_FLOWS.STOCK);
}
  
function isMobileFlow(flow: string | null): boolean {
    if (!flow) return false;
    
    const mobileFlows = [
        ORDER_FLOWS.CANNALEO_PRE_SELECTED,
        ORDER_FLOWS.MULTIPLE_PRODUCTS,
        ORDER_FLOWS.FLOWZZ_PRE_SELECTED,
        ORDER_FLOWS.OTC_ONLY,
    ];
    
    return mobileFlows.includes(flow) || flow.includes(ORDER_FLOWS.STOCK);
}


// ============================================================================
// Cart View Component (shared between mobile and desktop)
// ============================================================================

interface CartViewSectionProps {
    country: string;
    language: string;
    orderWindowDetails: OrderWindowDetails;
    shippingFee: number;
    isCannabisCatalog: boolean;
    productsGroupedByPharmacy: any[];
    onExpandProducts?: () => void;
    onTooltipOpen?: (tooltipId: string) => void;
    isMobile?: boolean;
    showDelayedCaptureDisclaimer?: boolean;
}
  
const CartViewSection: React.FC<CartViewSectionProps> = ({ country, language, orderWindowDetails, shippingFee, isCannabisCatalog, productsGroupedByPharmacy, onExpandProducts, onTooltipOpen, isMobile = false, showDelayedCaptureDisclaimer = false }) => {
    const countryUpper = country?.toUpperCase() || '';
    const { getCart } = useCartContext();
    const cartProducts = getCart()?.products;
    
    if (shouldShowPriceBreakdown(countryUpper, cartProducts, { isSubsidized: orderWindowDetails.isSubsidized })) {
        return (
            <CartViewDE
                trackFinalCartPrice={true}
                showProductsQuantityControl={false}
                shippingFee={shippingFee}
                isExpressShipping={orderWindowDetails.isExpressShipping}
                isCannabisCatalog={isCannabisCatalog}
                productsGroupedByPharmacy={productsGroupedByPharmacy}
                onExpandProducts={onExpandProducts}
                onTooltipOpen={onTooltipOpen}
                OTCAddonsTrigger={undefined}
                showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer}
                orderShippingType={orderWindowDetails.orderShippingType}
            />
        );
    }

    return (
        <CartView
            trackFinalCartPrice={true}
            showProductsQuantityControl={false}
            shippingFee={shippingFee}
            isExpressShipping={orderWindowDetails.isExpressShipping}
            OTCAddonsTrigger={undefined}
            showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer}
        />
    );
};
  

// ============================================================================
// Mobile Cart View Export
// ============================================================================
  
export const MobileCartView: React.FC<Omit<OrderSidebarProps, 'setRxProductFinalPrice' | 'rxProductFinalPrice'>> = ({ country, language, orderState, orderWindowDetails, shippingFee, isCannabisCatalog, productsGroupedByPharmacy, onExpandProducts, onTooltipOpen, showDelayedCaptureDisclaimer = false }) => {
    if (!isMobileFlow(orderState?.flow)) {
        return null;
    }
  
    const countryUpper = country?.toUpperCase() || '';
  
    return (
        <>
        <div className="lg:hidden">
            <CartViewSection
                country={country}
                language={language}
                orderWindowDetails={orderWindowDetails}
                shippingFee={shippingFee}
                isCannabisCatalog={isCannabisCatalog}
                productsGroupedByPharmacy={productsGroupedByPharmacy}
                onExpandProducts={onExpandProducts}
                onTooltipOpen={onTooltipOpen}
                isMobile={true}
                showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer}
            />
        </div>
        <div className={`trusted-shops-badge-mobile ${!TRUSTEDSHOP_COUNTRIES.includes(countryUpper) ? 'hidden' : ''}`}>
            <TrustedShopsBadge className="mt-4" country={country} language={language} />
        </div>
        </>
    );
};


// ============================================================================
// Order Sidebar Component
// ============================================================================
  
const OrderSidebar: React.FC<OrderSidebarProps> = ({ country, language, orderState, orderWindowDetails, OTCProducts, otcDiscount, shippingFee, currencyRate, currencySign, rxProductFinalPrice, setRxProductFinalPrice, isCannabisCatalog, testLabSampleType, productsGroupedByPharmacy, onExpandProducts, onTooltipOpen, showDelayedCaptureDisclaimer = false }) => {
    const countryUpper = country?.toUpperCase() || '';
    const { getCart } = useCartContext();
    const showPriceBreakdown = shouldShowPriceBreakdown(countryUpper, getCart()?.products, { isSubsidized: orderWindowDetails.isSubsidized });
    // CH has no price breakdown but still gets the test-lab FAQ for test-lab orders
    const showCartFaq = showPriceBreakdown || (countryUpper === SWITZERLAND && !!testLabSampleType);
  
    // New flow: CartView layout
    if (isNewFlow(orderState?.flow)) {
        return (
            <>
            <p className="orderWindow-container-title">
                {translator.translate(language, 'default', 'OrderWindow/title')}
            </p>
            <div className="hidden lg:block">
                <CartViewSection
                country={country}
                language={language}
                orderWindowDetails={orderWindowDetails}
                shippingFee={shippingFee}
                isCannabisCatalog={isCannabisCatalog}
                productsGroupedByPharmacy={productsGroupedByPharmacy}
                onExpandProducts={onExpandProducts}
                onTooltipOpen={onTooltipOpen}
                showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer}
                />
            </div>
            {TRUSTEDSHOP_COUNTRIES.includes(countryUpper) && (
                <div className={`trusted-shops-badge-desktop ${!COUNTRIES_WITH_TREATMENT_FEE.includes(countryUpper) ? 'hidden' : ''}`}>
                <TrustedShopsBadge className="mt-4" country={country} language={language} />
                </div>
            )}
            {showCartFaq && <CartViewFAQ flow={orderState.flow} sampleType={testLabSampleType} isCannabis={isCannabisCatalog} />}
            </>
        );
    }
  
    // Legacy flow: OrderWindow layout
    return (
        <>
        <OrderWindow
        itemImageUrl={orderWindowDetails.itemImageUrl}
        itemTitle={orderWindowDetails.itemTitle}
        Price={orderWindowDetails.Price}
        serviceFee={orderWindowDetails.serviceFee}
        isSubsidized={orderWindowDetails.isSubsidized}
        shippingFee={orderWindowDetails.shippingFee}
        itemQuantity={orderWindowDetails.itemQuantity}
        treatmentFee={orderWindowDetails.treatmentFee}
        subscriptionDiscount={orderWindowDetails.discount}
        couponDiscount={orderWindowDetails.bonusValue}
        setRxProductFinalPrice={setRxProductFinalPrice}
        isSelfPickup={orderWindowDetails.isSelfPickup}
        isCannabisIDAddedToOrder={orderWindowDetails.isCannabisIDAddedToOrder}
        disclaimer={orderWindowDetails.disclaimer}
        cannabisIdCardCost={orderWindowDetails.cannabisIdCardCost}
        onlyPrescriptionForPayment={orderWindowDetails.onlyPrescription}

        showSubscriptionPlans={false}
        selectedPlanIndex={0}
        updateDispatch={() => {}}
        autoBonusValue={0}
        catalogSettings={undefined}
        />
    
        {OTCProducts.length > 0 && (
        <OrderOTCAddons
            title={translator.translate(language, 'default', 'OrderSummary/otc_addons_price')}
            shouldShowOtcAddonsModalButton={false}
            currencyRate={parseFloat(currencyRate)}
            currencySign={currencySign}
            selectedOtcAddons={OTCProducts.map((product) => ({
                quantity: product.quantity,
                productItem: {
                    id: product.id,
                    title: product.title,
                    price: product.price / product.quantity,
                    imgSrc: product.imgSrc,
                    custom_quantity_title: product.custom_quantity_title,

                    official_price: product.official_price,  // divide by quantity?
                    quantity: product.quantity,
                },
            }))}
        />
        )}
    
        {OTCProducts.length > 0 && !orderWindowDetails.isCannabisIDAddedToOrder && (
        <OrderSummary
            rxProductFinalPrice={rxProductFinalPrice}
            otcAddonsTotalPrice={OTCProducts.reduce((total, product) => {
                const price = product?.price ?? 0;
                return total + price * parseFloat(currencyRate);
            }, 0)}
            currencySign={currencySign}
            otcDiscount={otcDiscount}
        />
        )}
        </>
    );
};

export default OrderSidebar;